import {
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { MoreHoriz } from "@material-ui/icons";
import { useEffect, useReducer, useState } from "react";
import {
  FormDataConsumer,
  NumberInput,
  RadioButtonGroupInput,
  required,
  SelectInput,
  TextInput,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import { GeolocationApiFactory } from "../../../common/data/service-proxies";
import { useAppData } from "../../../common/hooks/useAppData";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import {
  CompactGoogleMapLocationSelector,
  SelectedMapLocation,
} from "../../locations/CompactGoogleMapLocationSelector";
import classes from "./EventLocationInput.module.css";
import { Coordinates, EventLocationSearch } from "./EventLocationSearch";

interface Props {
  record?: any;
}

interface EventLocationInputComponentProps {
  locationSource: EventLocationSource;
  stateId?: number;
  countryId?: number;
  latitude?: string;
  longitude?: string;
  driversLocationDescription?: string;
}

enum EventLocationSource {
  ManualInput = "ManualInput",
  SelectedFromMap = "SelectedFromMap",
}

const SET_COORDINATES = "SET_COORDINATES";

const createSetCoordinatesAction = (coordinates?: Coordinates) => {
  return {
    type: SET_COORDINATES,
    payload: coordinates,
  };
};

const coordinatesReducer = (
  prev: Coordinates | undefined,
  action: ReturnType<typeof createSetCoordinatesAction>
) => {
  if (!action.payload) {
    return undefined;
  }

  return { ...prev, ...action.payload };
};

const EventLocationInputComponent = (
  props: EventLocationInputComponentProps
) => {
  const {
    locationSource,
    stateId,
    countryId,
    latitude,
    longitude,
    driversLocationDescription,
    ...rest
  } = props;

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [proposedCoordinates, dispatch] = useReducer(
    coordinatesReducer,
    undefined
  );

  const countries = useAppData((x) => x.countries);
  const states = countries.find((x) => x.id === countryId)?.states ?? [];
  const axios = useAxiosInstance();

  const form = useForm();

  const isManualInput = locationSource === EventLocationSource.ManualInput;
  const open =
    dialogOpen && locationSource === EventLocationSource.SelectedFromMap;

  const {
    values: { latitude: lat, longitude: lng, geolocation },
  } = useFormState();

  useEffect(() => {
    if (!lat || !lng) {
      return;
    }

    if (!axios) {
      return;
    }

    GeolocationApiFactory(undefined, axios?.defaults.baseURL, axios)
      .apiAppGeolocationGeolocationGet(lat, lng)
      .then((response) => {
        if (!response.data) {
          return;
        }

        const usa = countries.find((x) => x.name === "USA");
        const state = usa?.states?.find(
          (x) => x.stateCode === response.data.state
        );

        const geolocation =
          response.data.distance && response.data.distance > 0
            ? `${response.data.distance}mi ${response.data.direction} ${response.data.name}, ${response.data.state}`
            : `${response.data.name}, ${response.data.state}`;
        form.change("countryId", usa?.id);
        form.change("stateId", state?.id);
        form.change("geolocation", geolocation);
      });
  }, [lat, lng, geolocation, axios, countries, form]);

  useEffect(() => {
    if (!isManualInput) {
      return;
    }

    if (!driversLocationDescription) {
      return;
    }

    const last2 = driversLocationDescription.trimEnd().slice(-2);
    let foundCountry, foundState;
    for (const country of countries) {
      for (const state of country.states ?? []) {
        if (last2 === state.stateCode) {
          foundCountry = country;
          foundState = state;
          break;
        }
      }
    }

    if (foundCountry && foundState) {
      form.change("countryId", foundCountry.id);
      form.change("stateId", foundState.id);
    }
  }, [form, isManualInput, driversLocationDescription, countries]);

  const handleAccept = (location: SelectedMapLocation) => {
    handleDialogClose();

    form.change("latitude", `${location.lat?.toFixed(6)}`);
    form.change("longitude", `${location.lng?.toFixed(6)}`);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const getEndAdornment = () => {
    return (
      <InputAdornment position="end">
        <IconButton
          className={classes["clear-button"]}
          aria-label="Browse"
          title="Browse"
          disableRipple
          onClick={handleDialogOpen}>
          <MoreHoriz />
        </IconButton>
      </InputAdornment>
    );
  };

  return (
    <>
      {isManualInput ? (
        <TextInput
          {...rest}
          style={{ width: "50%" }}
          source="driversLocationDescription"
          label="Geolocation"
          validate={required()}
        />
      ) : (
        <>
          <div className="ra-input ra-input-undefined">
            <div className="two-inputs-container">
              <NumberInput
                {...rest}
                validate={required()}
                source="latitude"
                label="Latitude"
                step="0.0001"
              />
              <NumberInput
                {...rest}
                validate={required()}
                source="longitude"
                label="Longitude"
                step="0.0001"
              />
            </div>
          </div>
          <TextInput
            {...rest}
            style={{ width: "50%" }}
            source="geolocation"
            label="Geolocation"
            validate={required()}
            inputProps={{ readOnly: true }}
            InputProps={{
              endAdornment: getEndAdornment(),
            }}
          />
        </>
      )}
      <div className="ra-input ra-input-undefined">
        <div className="two-inputs-container">
          <SelectInput
            {...rest}
            validate={required()}
            source="countryId"
            label="Country"
            choices={countries}
          />
          <SelectInput
            {...rest}
            validate={required()}
            source="stateId"
            label="State"
            choices={states}
          />
        </div>
      </div>
      <Dialog fullWidth maxWidth="md" open={open} onClose={handleDialogClose}>
        <DialogTitle>
          <div className={classes["dialog-title"]}>
            Pick a location from the map
            <div className={classes["dialog-title-toolbar"]}>
              <div className={classes["dialog-title-search"]}>
                <EventLocationSearch
                  onGeolocationFound={(x) =>
                    dispatch(createSetCoordinatesAction(x))
                  }
                />
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent className={classes["dialog-content"]}>
          <Card className={classes["map-container"]}>
            <CompactGoogleMapLocationSelector
              fullscreenControl={false}
              onAccept={handleAccept}
              centerLat={proposedCoordinates?.lat}
              centerLng={proposedCoordinates?.lng}
            />
          </Card>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const EventLocationInput = (props: Props) => {
  return (
    <div className={classes["location-picker"]}>
      <div className={`ra-input ${classes["location-source-input"]}`}>
        <RadioButtonGroupInput
          source="locationSource"
          label="Specify location"
          choices={[
            {
              id: EventLocationSource.SelectedFromMap,
              name: "By picking it from the map",
            },
            {
              id: EventLocationSource.ManualInput,
              name: "By describing it manually",
            },
          ]}
          defaultValue={EventLocationSource.SelectedFromMap}
        />
      </div>
      <FormDataConsumer>
        {({ formData, ...rest }) => {
          const {
            locationSource,
            stateId,
            countryId,
            latitude,
            longitude,
            driversLocationDescription,
          } = formData;
          return (
            <EventLocationInputComponent
              locationSource={locationSource}
              stateId={stateId}
              countryId={countryId}
              latitude={latitude}
              longitude={longitude}
              driversLocationDescription={driversLocationDescription}
              {...rest}
              {...props}
            />
          );
        }}
      </FormDataConsumer>
    </div>
  );
};
